import { useId } from 'react';
import { cn } from '../lib/cn.js';

/**
 * Valence, not direction (DESIGN_SYSTEM section 9.1): tell the sparkline whether a rising line is
 * good or bad and it picks --ok / --brand; `neutral` is always --mute; `hero` is always --brand.
 */
export type SparklineValence = 'up-bad' | 'up-good' | 'neutral' | 'hero';

export interface SparklineProps {
  values: number[];
  /** `hero` 100x30 (default) or `table` 60x14. */
  size?: 'hero' | 'table';
  valence?: SparklineValence;
  /** CSS width/height; defaults to w-full h-8 (hero) or w-[60px] h-[14px] (table). */
  className?: string;
  /** Accessible summary; defaults to `first → last`. */
  label?: string;
}

const VIEW: Record<NonNullable<SparklineProps['size']>, { w: number; h: number }> = {
  hero: { w: 100, h: 30 },
  table: { w: 60, h: 14 },
};

export function sparklineColor(values: number[], valence: SparklineValence): string {
  if (valence === 'hero') return 'var(--brand)';
  if (valence === 'neutral' || values.length < 2) return 'var(--mute)';
  const first = values[0] ?? 0;
  const last = values[values.length - 1] ?? 0;
  if (last === first) return 'var(--mute)';
  const rising = last > first;
  if (valence === 'up-bad') return rising ? 'var(--brand)' : 'var(--ok)';
  return rising ? 'var(--ok)' : 'var(--brand)';
}

/** One 1px polyline, no fill, no axis, non-scaling stroke. */
export function Sparkline({ values, size = 'hero', valence = 'hero', className, label }: SparklineProps) {
  const id = useId();
  const { w, h } = VIEW[size];
  const n = values.length;
  const min = n ? Math.min(...values) : 0;
  const max = n ? Math.max(...values) : 1;
  const range = max - min || 1;
  const pad = 1;
  const points = values
    .map((v, i) => {
      const x = n === 1 ? w / 2 : (i / (n - 1)) * w;
      const y = pad + (1 - (v - min) / range) * (h - pad * 2);
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(' ');
  const summary = label ?? (n ? `${values[0]} to ${values[n - 1]}` : 'no data');
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      role="img"
      aria-labelledby={`${id}-t`}
      className={cn(size === 'hero' ? 'h-8 w-full' : 'h-[14px] w-[60px]', 'block overflow-visible', className)}
    >
      <title id={`${id}-t`}>{summary}</title>
      {n > 0 ? (
        <polyline
          points={points}
          fill="none"
          stroke={sparklineColor(values, valence)}
          strokeWidth={1}
          vectorEffect="non-scaling-stroke"
          strokeLinejoin="round"
        />
      ) : null}
    </svg>
  );
}
